import { CommentatorReaction } from "@/components/commentator-reaction"
import { RaceShield } from "@/components/race-shield"
import { RevealBlock } from "@/components/reveal-block"
import { SegmentHeader } from "@/components/segment-header"
import { SlamBlock } from "@/components/slam-block"

import { CommentaryPanel } from "./commentary-panel"
import { RaceSpotlightCard } from "./race-spotlight-card"

/** Six archetype spotlights — one per playstyle family. */
const SPOTLIGHTS = [
  {
    race: "orc",
    flavor:
      "Built like a brick outhouse and twice as mean. The default answer to every problem is violence.",
  },
  {
    race: "wood_elf",
    flavor:
      "Fast, fragile, and insufferably graceful. Score before anyone touches you — because one hit and you're done.",
  },
  {
    race: "halfling",
    flavor:
      "No business being on a Blood Bowl pitch. They know it. They don't care. Treemen help.",
  },
  {
    race: "shambling_undead",
    flavor:
      "Slow, tough, and they never stay dead. Grind you down over 16 turns and walk away with the ball.",
  },
  {
    race: "skaven",
    flavor:
      "Rat-men with a death wish and a Gutter Runner who can score from anywhere. Chaotic, fast, fragile.",
  },
  {
    race: "lizardmen",
    flavor:
      "Dinosaurs playing football. Saurus hit like trucks, Skinks run like the wind. Best of both worlds.",
  },
]

const ALL_RACES = [
  "orc",
  "human",
  "skaven",
  "dwarf",
  "dark_elf",
  "shambling_undead",
  "wood_elf",
  "lizardmen",
  "chaos_chosen",
  "nurgle",
  "high_elf",
  "halfling",
  "vampire",
  "necromantic_horror",
  "goblin",
  "norse",
  "amazon",
  "tomb_kings",
  "chaos_renegades",
  "khorne",
  "underworld_denizens",
  "chaos_dwarf",
  "imperial_nobility",
  "bretonnian",
  "old_world_alliance",
  "elven_union",
  "ogre",
  "snotling",
  "gnome",
  "black_orc",
]

const COMMENTARY = [
  {
    commentatorKey: "vaeltharion_the_unimpressed",
    name: "Vaeltharion",
    mood: "smug",
    line: "Thirty races. Orcs, elves, the risen dead, rat-men, halflings who have no business being here. Pick one. Preferably something with dignity.",
    side: "left" as const,
  },
  {
    commentatorKey: "mortifax_greel",
    name: "Mortifax Greel",
    mood: "amused",
    line: "Each one dies differently. I find that... distinctive.",
    side: "right" as const,
  },
]

const TIMING = {
  spotlightStart: 0.1, // first spotlight card
  spotlightStagger: 0.08, // between spotlight cards
  commentaryStart: 0.1, // first commentary line
  commentaryStagger: 0.15, // between commentary lines
  fullGrid: 0.2, // 30-race grid reveal
} as const

interface RaceRosterSectionProps {
  portraitUrls: Record<string, string>
}

export function RaceRosterSection({ portraitUrls }: RaceRosterSectionProps) {
  return (
    <>
      {/* ── Header + spotlight grid ── */}
      <div className="mx-auto max-w-5xl px-4">
        <RevealBlock>
          <SegmentHeader label="Choose Your Team" />
        </RevealBlock>

        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {SPOTLIGHTS.map((s, i) => (
            <RevealBlock
              key={s.race}
              delay={TIMING.spotlightStart + i * TIMING.spotlightStagger}
            >
              <RaceSpotlightCard race={s.race} flavor={s.flavor} />
            </RevealBlock>
          ))}
        </div>
      </div>

      {/* ── Commentary ── */}
      <CommentaryPanel>
        {COMMENTARY.map((entry, i) => (
          <SlamBlock
            key={entry.commentatorKey}
            from={entry.side}
            delay={TIMING.commentaryStart + i * TIMING.commentaryStagger}
          >
            <CommentatorReaction
              commentatorKey={entry.commentatorKey}
              name={entry.name}
              mood={entry.mood}
              line={entry.line}
              portraitUrl={
                portraitUrls[`${entry.commentatorKey}_${entry.mood}`] ?? null
              }
              side={entry.side}
            />
          </SlamBlock>
        ))}
      </CommentaryPanel>

      {/* ── Full race grid ── */}
      <RevealBlock delay={TIMING.fullGrid} className="mx-auto max-w-5xl px-4">
        <div className="flex flex-col items-center gap-3">
          {[
            ALL_RACES.slice(0, 10),
            ALL_RACES.slice(10, 18),
            ALL_RACES.slice(18, 24),
            ALL_RACES.slice(24, 28),
            ALL_RACES.slice(28, 30),
          ].map((row, ri) => (
            <div key={ri} className="flex justify-center gap-3">
              {row.map((race) => (
                <RaceShield key={race} race={race} size="md" />
              ))}
            </div>
          ))}
        </div>
        <p
          className="font-display mt-3 text-center text-[10px] tracking-[0.2em] uppercase"
          style={{ color: "oklch(0.45 0.02 55)" }}
        >
          30 races. Pick your poison.
        </p>
      </RevealBlock>
    </>
  )
}
